import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { StoreService } from './store.service';
import { ToolsService } from './tools.service';
import { MessageService } from './message.service';
import { HttpHeaders, HttpClient, HttpResponse } from '@angular/common/http';
import { IHttpOptions } from '../interfaces/http-options';
import { IHttpPostResponse } from '../interfaces/http-post-response';
import * as tools from '../tools';

@Injectable({
	providedIn: 'root',
})
export class DataService {

	private appId: string;
	private token: string;
	private apiUrl: string;

	constructor(
		private http: HttpClient,
		private toolsService: ToolsService,
		public storeService: StoreService
	) {
		this.apiUrl =  `${window.location.origin}/KioskAPI/api`;;
	}

	private async getHttpOptions(): Promise<IHttpOptions> {

		//const token = await this.getToken();

		const httpOptions = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
			}),
			withCredentials: true
		};

		return httpOptions;
	}

	/** Gets a login token. */
	private async getToken(): Promise<string> {
		// if (!this.token) {
		//   await this.setApiUrl();
		//   const loginRes = await this.login();
		//   this.token = loginRes.token;
		// }

		return this.token;
	}

	/**
	 * Logs in the user
	 * @param orgId the organization ID
	 */
	private async login(): Promise<any> {

		const httpOptions = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				'Authorization': 'Basic Y2hyaXNAZHJvbmVzZW5zZS5jb206UEBzc3dvcmQx', // THIS IS ONLY HARD-CODED UNTIL WE HAVE A LOGIN PAGE
				'appId': this.appId
			})
		};

		this.storeService.loading = true;
		const response = await this.http.post(`${this.apiUrl}/Users/login`, null, httpOptions)
			.pipe(
				catchError(this.toolsService.handleError())
			).toPromise();

		this.storeService.loading = false;
		return response;
	}

	/**
	 * Type casting works great for fields, but it won't allow properties and/or methods to work.
	 * For that, you have to physically new up the type, which is what this method does.
	 * @param data a list of data objects
	 * @param classObj type type to create
	 */
	private createTypedObjects<TReturnType>(data: any, classObj: any) {
		if (data && data.length && classObj) {
			for (let i = 0; i < data.length; i++) {
				const newObj = tools.create<TReturnType>(classObj);

				data[i] = Object.assign(newObj, data[i]);
			}
		}
	}

	/**
	 * Gets a single, typed record from the API.
	 * @param url the url of the GET route
	 * @param classObj the type of of the objects that will be created
	 */
	async get<TReturnType>(url: string, classObj?: any, useApiUrl: boolean = true, showToasts: boolean = true): Promise<IHttpPostResponse<TReturnType>> {

		const httpOptions = await this.getHttpOptions();
		const urlPath = (useApiUrl) ? `${this.apiUrl}${url}` : url;

		this.storeService.loading = true;

		const response = await this.http.get<IHttpPostResponse<TReturnType>>(urlPath, httpOptions)
			.pipe(
				catchError(this.toolsService.handleError<IHttpPostResponse<TReturnType>>(showToasts)),
				map(res => {
					// typecasting works great for fields, but it won't allow properties and/or methods to work
					// for that, you have to physically new up the type
					if (res && res.data && classObj) {
						// create a new object of the given type
						const newObj = tools.create<TReturnType>(classObj);

						res.data = Object.assign(newObj, res.data);
					}

					return res;
				})
			)
			.toPromise();

		this.storeService.loading = false;
		return response;
	}

	/**
	 * Gets a list of typed records from the API.
	 * @param url the url of the GET route
	 * @param classObj the type of of the objects that will be created
	 */
	async getDataList<TReturnType>(url: string, classObj?: any, useApiUrl: boolean = true, showToasts: boolean = true): Promise<IHttpPostResponse<TReturnType[]>> {

		const httpOptions = await this.getHttpOptions();
		const urlPath = (useApiUrl) ? `${this.apiUrl}${url}` : url;

		this.storeService.loading = true;

		const response = await this.http.get<IHttpPostResponse<TReturnType[]>>(urlPath, httpOptions)
			.pipe(
				catchError(this.toolsService.handleError<IHttpPostResponse<TReturnType[]>>(showToasts)),
				map(res => {
					this.createTypedObjects(res.data, classObj);

					return res;
				})
			).toPromise();

		this.storeService.loading = false;
		return response;
	}

	/**
	 * Gets a list of typed records from the API, along with information from the headers.
	 * @param url the url of the GET route
	 * @param classObj the type of of the objects that will be created
	 */
	async getDataListWithHeaders<TReturnType>(url: string, classObj?: any, useApiUrl: boolean = true, showToasts: boolean = true): Promise<HttpResponse<TReturnType[]>> {

		const httpOptions = await this.getHttpOptions();
		const urlPath = (useApiUrl) ? `${this.apiUrl}${url}` : url;

		this.storeService.loading = true;

		const response = await this.http.get<TReturnType[]>(urlPath, {
			headers: httpOptions.headers,
			observe: 'response'
		})
			.pipe(
				catchError(this.toolsService.handleError<HttpResponse<TReturnType[]>>(showToasts)),
				map(res => {
					if (res) {
						this.createTypedObjects(res.body, classObj);
					}

					return res;
				})
			).toPromise();

		this.storeService.loading = false;
		return response;
	}

	/**
	 * Executes a POST request against the API.
	 * @param url the url of the POST route
	 * @param postData the data to post
	 * @param classObj the type of of the objects that will be posted and then returned
	 */
	async post<TReturnType>(url: string, postData?: any, classObj?: any, useApiUrl: boolean = true, showToasts: boolean = true, headers: IHttpOptions = null): Promise<IHttpPostResponse<TReturnType>> {

		let httpOptions = await this.getHttpOptions();

		if (headers) {
			httpOptions = headers;
		}
		const urlPath = (useApiUrl) ? `${this.apiUrl}${url}` : url;

		this.storeService.loading = true;

		const response = await this.http.post<IHttpPostResponse<TReturnType>>(urlPath, postData, httpOptions)
			.pipe(
				catchError(this.toolsService.handleError<IHttpPostResponse<TReturnType>>(showToasts)),
				map(res => {
					if (res && res.data && classObj) {
						// create a new object of the given type
						const newObj = tools.create<TReturnType>(classObj);

						res.data = Object.assign(newObj, res.data);
					}

					return res;
				})
			).toPromise();

		this.storeService.loading = false;
		return response;
	}

	/**
	 * Executes a POST request against the API.
	 * @param url the url of the POST route
	 * @param postData the data to post
	 */
	async put(url: string, postData?: any, useApiUrl: boolean = true, showToasts: boolean = true): Promise<any> {

		const httpOptions = await this.getHttpOptions();
		const urlPath = (useApiUrl) ? `${this.apiUrl}${url}` : url;

		this.storeService.loading = true;

		const response = await this.http.put(urlPath, postData, httpOptions)
			.pipe(
				catchError(this.toolsService.handleError())
			).toPromise();

		this.storeService.loading = false;
		return response;
	}

	/**
	 * Posts a list of strongly-typed records to the API and returns the updated list.
	 * @param url the url of the POST route
	 * @param postData the data to post
	 * @param classObj the type of of the objects that will be posted and then returned
	 */
	async postDataList<TReturnType>(url: string, postData?: any, classObj?: any, useApiUrl: boolean = true, showToasts: boolean = true): Promise<TReturnType[]> {

		const httpOptions = await this.getHttpOptions();
		const urlPath = (useApiUrl) ? `${this.apiUrl}${url}` : url;

		this.storeService.loading = true;

		const response = await this.http.post<TReturnType[]>(urlPath, postData, httpOptions)
			.pipe(
				catchError(this.toolsService.handleError<TReturnType[]>(showToasts)),
				map(res => {
					this.createTypedObjects(res, classObj);

					return res;
				})
			).toPromise();

		this.storeService.loading = false;
		return response;
	}

	/**
	 * Executes a DELETE request against the given url.
	 * @param url the url of the DELETE route
	 */
	async delete(url: string, useApiUrl: boolean = true, showToasts: boolean = true): Promise<any> {

		const httpOptions = await this.getHttpOptions();
		const urlPath = (useApiUrl) ? `${this.apiUrl}${url}` : url;

		this.storeService.loading = true;

		const response = await this.http.delete(urlPath, httpOptions)
			.pipe(
				catchError(this.toolsService.handleError(showToasts))
			).toPromise();

		this.storeService.loading = false;
		return response;
	}

	/**
	 * This is only here for testing new API (GET) endpoints.
	 * @param url the url of the GET route
	 */
	async getRandomApiData(url: string, useApiUrl: boolean = true, showToasts: boolean = true): Promise<HttpResponse<any>> {

		const httpOptions = await this.getHttpOptions();
		const urlPath = (useApiUrl) ? `${this.apiUrl}${url}` : url;

		this.storeService.loading = true;

		const response = await this.http.get(urlPath, {
			headers: httpOptions.headers,
			observe: 'response'
		}).toPromise();

		this.storeService.loading = false;
		return response;
	}

	/**
	 * Use this to get an authorization header for 3rd party controls,
	 * like file upload utilities, that need custom http calls.
	 **/
	async getHttpAuthorizationHeader(): Promise<HttpHeaders> {

		const token = await this.getToken();

		const httpHeaders = new HttpHeaders({
			'Authorization': `Bearer RQXJIepdpUeSZnTxkSF7Jg==`
		});

		return httpHeaders;
	}

}
